import T from 'prop-types';
import i18n from 'i18next';
import { BatchResponse } from 'helpers/propTypes';
import {
  BATCH_TARGET_TYPE_DECIMAL,
  BATCH_TARGET_TYPE_GRINDING_ENERGY,
  BATCH_TARGET_TYPE_NUMBER_CYCLES,
  BATCH_TARGET_TYPE_SPECIFIC_ENERGY,
  BATCH_TARGET_TYPE_TIME
} from 'attrs/batch';
import {
  dateToLocaleStr,
  getMachineType,
  getMillBeadMaterialType,
  getMillGrindingTechnology,
  getMillInnerLinerMaterial,
  getToFixedValue,
  isNullUndefinedOrEmpty,
  timeToString
} from 'helpers/utils';
import { defaultEmptyCellValue } from '../../../helpers/stringHandler';

const getData = values => {
  if (!values) {
    return null;
  }

  const data = values || {};
  data.details = values.details || {};
  data.target = values.target || {};
  data.property = values.property || {};
  data.machine = values.machine || {};
  data.machine.mechanical_config = values.machine.mechanical_config || {};

  const batchStatusData = {
    localeKey: 'batch_status',
    rows: [
      {
        id: 'status-row1',
        row: [
          {
            localeKey: 'status',
            value: data.details.status
          },
          {
            localeKey: 'progress',
            value: Number.isInteger(data.details.progress)
              ? data.details.progress
              : getToFixedValue(data.details.progress, 0)
          },
          {
            localeKey: 'batch_length',
            value: timeToString(data.details.length)
          },
          {
            localeKey: 'total_batch_size',
            value: getToFixedValue(data.details.size, 1) || defaultEmptyCellValue
          },
          {
            localeKey: 'start_time',
            value: dateToLocaleStr(data.details.date.start)
          },
          {
            localeKey: 'end_time',
            value: dateToLocaleStr(data.details.date.end)
          }
        ]
      }
    ]
  };

  const invalidValue = value => [null, 'NONE', 'UNKNOWN', ''].includes(value);

  const getGrindingTargetLabel = (parentValue, childValue) => {
    const labelList = ['NONE', 'TIME', 'GRINDING_ENERGY', 'SPECIFIC_ENERGY', 'NUMBER_CYCLES', 'PRODUCT_RATE'];
    let newLabelValue;
    if (invalidValue(parentValue)) {
      newLabelValue = defaultEmptyCellValue;
    } else if (!!childValue && labelList.includes(childValue)) {
      newLabelValue = i18n.t(`batch_records.target.${childValue.toLowerCase()}`);
    }
    return newLabelValue || childValue || defaultEmptyCellValue;
  };

  const getGrindingTarget = value => getGrindingTargetLabel(value, data.target.type);

  const getTarget = (type, value) => {
    if (invalidValue(value) || invalidValue(type)) {
      return defaultEmptyCellValue;
    }

    if (type === BATCH_TARGET_TYPE_TIME) {
      return typeof value === 'string' ? value : timeToString(value);
    }

    return getToFixedValue(value, BATCH_TARGET_TYPE_DECIMAL[type]);
  };

  const batchDetailsData = {
    localeKey: 'batch_details',
    rows: [
      {
        id: 'details-recipe-temp',
        localeKey: 'recipe_temperature',
        row: [
          {
            localeKey: 'recipe',
            value: data.details.recipe || defaultEmptyCellValue
          },
          {
            localeKey: 'max_temp',
            value: getToFixedValue(data.details.max_allowed_temperature, 1)
          }
        ]
      },
      {
        id: 'details-target',
        localeKey: 'target',
        row: [
          {
            localeKey: 'grinding_target',
            value: getGrindingTarget(data.target.type)
          },
          {
            localeKey: 'target',
            value: data.target.value_string ?? getTarget(data.target.type, data.target.value)
          }
        ]
      },
      {
        id: 'details-energy',
        localeKey: 'grinding',
        row: [
          {
            localeKey: 'grinding_energy',
            value: getToFixedValue(
              data.property.grinding_energy,
              BATCH_TARGET_TYPE_DECIMAL[BATCH_TARGET_TYPE_GRINDING_ENERGY]
            )
          },
          {
            localeKey: 'specific_energy',
            value: getToFixedValue(
              data.property.specific_energy,
              BATCH_TARGET_TYPE_DECIMAL[BATCH_TARGET_TYPE_SPECIFIC_ENERGY]
            )
          },
          {
            localeKey: 'total_grinding_time',
            value: data.property.total_grinding_time
              ? timeToString(data.property.total_grinding_time)
              : defaultEmptyCellValue
          },
          {
            localeKey: 'cycles',
            value: getToFixedValue(data.details.cycles, BATCH_TARGET_TYPE_DECIMAL[BATCH_TARGET_TYPE_NUMBER_CYCLES])
          }
        ]
      }
    ]
  };

  const machineType = getMachineType(data.machine.type);
  const grindingTechnology = getMillGrindingTechnology(data.machine.mechanical_config.grinding_technology);
  const innerLinerMaterial = getMillInnerLinerMaterial(data.machine.mechanical_config.inner_liner_material);
  const beadMaterialType = getMillBeadMaterialType(data.machine.mechanical_config.bead_material);

  let sambaSystemKey = 'batch_records.details.samba_system_false';
  if ([null, undefined].includes(data.machine.mechanical_config.samba_system)) {
    sambaSystemKey = 'batch_records.details.samba_system_undefined';
  } else if (data.machine.mechanical_config.samba_system === true) {
    sambaSystemKey = 'batch_records.details.samba_system_true';
  }

  const machineDetailsData = {
    localeKey: 'machine_details',
    rows: [
      {
        id: 'machine-row-1',
        row: [
          {
            localeKey: 'commission_no',
            value: data.machine.commission_number
          },
          {
            localeKey: 'name',
            value: data.machine.name || i18n.t('machines.overview.machine_name_not_assigned')
          },
          {
            localeKey: 'brand',
            value: machineType.brand
          },
          {
            localeKey: 'mill_system',
            value: machineType.type
          },
          {
            localeKey: 'grinding_technology',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.grinding_technology)
              ? grindingTechnology.name
              : defaultEmptyCellValue
          },
          {
            localeKey: 'inner_liner_material',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.inner_liner_material)
              ? innerLinerMaterial.name
              : defaultEmptyCellValue
          },
          {
            localeKey: 'samba_system',
            value: i18n.t(sambaSystemKey)
          },
          {
            localeKey: 'sieve_size',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.sieve_size)
              ? data.machine.mechanical_config.sieve_size
              : defaultEmptyCellValue
          }
        ]
      },
      {
        id: 'machine-beads',
        localeKey: 'beads',
        row: [
          {
            localeKey: 'bead_material',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.bead_material)
              ? beadMaterialType.name
              : defaultEmptyCellValue
          },
          {
            localeKey: 'bead_size_1',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.beads_size_1)
              ? data.machine.mechanical_config.beads_size_1
              : defaultEmptyCellValue
          },
          {
            localeKey: 'bead_size_2',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.beads_size_2)
              ? data.machine.mechanical_config.beads_size_2
              : defaultEmptyCellValue
          },
          {
            localeKey: 'bead_filling_level',
            value: !isNullUndefinedOrEmpty(data.machine.mechanical_config.beads_filling_level)
              ? data.machine.mechanical_config.beads_filling_level
              : defaultEmptyCellValue
          }
        ]
      }
    ]
  };

  return { batchStatusData, batchDetailsData, machineDetailsData };
};

getData.propTypes = {
  values: T.shape(BatchResponse).isRequired
};

export { getData };
